import { Bounds, Vector } from '@ardoq/graph';
import { rectCenter, rectHeight, rectWidth } from '../misc/geometry';

export class Projection {
  public adjustToViewport(viewport: Bounds, dpr: number) {
    this.dpr = dpr;
    this._viewport = [...viewport];

    if (!isFinite(this.scale) && isFinite(this._window[0])) {
      this.adjustToBounds(this._window, 0);
    }
  }

  public adjustToBounds(wd: Bounds, margin: number) {
    this._window = [...wd];

    const vp = this.viewport;
    const wdSize = [rectWidth(wd), rectHeight(wd)];
    const vpSize = [rectWidth(vp) - 2 * margin, rectHeight(vp) - 2 * margin];

    if (vpSize[0] > 0 && vpSize[1] > 0) {
      const cwd = rectCenter(wd);
      const cvp = rectCenter(vp);

      const scale = Math.min(vpSize[0] / wdSize[0], vpSize[1] / wdSize[1]);
      const translation: Vector = [
        cvp[0] - cwd[0] * scale,
        cvp[1] - cwd[1] * scale,
      ];

      this.scale = scale;
      this.translation = translation;
    }
  }

  public zoomToPoint(center: Vector, scale: number) {
    const vp = this.projectPoint(center);

    this.scale = scale;
    this.translation = [vp[0] - center[0] * scale, vp[1] - center[1] * scale];
    //this._window = this.unprojectBounds(this._viewport);
  }

  public panTo(wd: Vector, vp: Vector) {
    // adjust the translation such that wd gets projected to vp
    this.translation = [vp[0] - wd[0] * this.scale, vp[1] - wd[1] * this.scale];
  }

  public panBy(worldDelta: Vector) {
    // shift the visible window by worldDelta in world units; preserves scale
    this.translation = [
      this.translation[0] - worldDelta[0] * this.scale,
      this.translation[1] - worldDelta[1] * this.scale,
    ];
  }

  public get viewport() {
    return this._viewport;
  }
  private _viewport: Bounds = [NaN, NaN, NaN, NaN];

  public get window() {
    return this.unprojectBounds(this._viewport);
  }
  public set window(value: Bounds) {
    this._window = [...value];
  }
  private _window: Bounds = [NaN, NaN, NaN, NaN];

  public projectScalar(wd: number) {
    return wd * this.scale;
  }

  public unprojectScalar(vp: number) {
    return vp / this.scale;
  }

  public projectPoint(wd: Vector): Vector {
    return [
      (wd[0] * this.scale + this.translation[0]) / this.dpr,
      (wd[1] * this.scale + this.translation[1]) / this.dpr,
    ];
  }

  public projectPointSnapped(wd: Vector): Vector {
    return [
      0.5 +
        Math.round((wd[0] * this.scale + this.translation[0]) / this.dpr - 0.5),
      0.5 +
        Math.round((wd[1] * this.scale + this.translation[1]) / this.dpr - 0.5),
    ];
  }

  public unprojectPoint(vp: Vector): Vector {
    return [
      (vp[0] * this.dpr - this.translation[0]) / this.scale,
      (vp[1] * this.dpr - this.translation[1]) / this.scale,
    ];
  }

  public projectBounds(wd: Bounds): Bounds {
    const a = this.projectPoint([wd[0], wd[1]]);
    const b = this.projectPoint([wd[2], wd[3]]);

    return [
      Math.min(a[0], b[0]),
      Math.min(a[1], b[1]),
      Math.max(a[0], b[0]),
      Math.max(a[1], b[1]),
    ];
  }

  public projectBoundsSnapped(wd: Bounds): Bounds {
    const a = this.projectPointSnapped([wd[0], wd[1]]);
    const b = this.projectPointSnapped([wd[2], wd[3]]);

    return [
      Math.min(a[0], b[0]),
      Math.min(a[1], b[1]),
      Math.max(a[0], b[0]),
      Math.max(a[1], b[1]),
    ];
  }

  public unprojectBounds(vp: Bounds): Bounds {
    const a = this.unprojectPoint([vp[0], vp[1]]);
    const b = this.unprojectPoint([vp[2], vp[3]]);

    return [
      Math.min(a[0], b[0]),
      Math.min(a[1], b[1]),
      Math.max(a[0], b[0]),
      Math.max(a[1], b[1]),
    ];
  }

  private dpr: number = 1;
  public scale: number = NaN;
  private translation: Vector = [NaN, NaN];
}
